import { useMemo } from 'react';
import { useUserProjectReplacementsQuery } from '@replacements/graphql/operation.generated';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { useCanReadTeamReplacements } from 'abilities/replacements';
import { EnhancedPhoneticReplacement } from 'store/ReplacementsSlice';

import useEvaluateStudioProjectVersionFeatures from 'components/Studio/useEvaluateStudioProjectVersionFeatures';
import { StudioProjectVersion } from '../../../../server/graphql/__generated.types';

interface UseProjectReplacementsOptions {
  projectId?: string;
  projectStudioVersion?: StudioProjectVersion;
  fetchPolicy?: WatchQueryFetchPolicy;
}
/*
 * Fetch replacements that are enabled for a project
 * sets default fetch policy to apollo's default of 'cache-first'
 */
const useProjectReplacements = (options?: UseProjectReplacementsOptions) => {
  const {
    projectId,
    projectStudioVersion,
    fetchPolicy = 'cache-first',
  } = options ?? {};
  const canReadTeamReplacements = useCanReadTeamReplacements();
  const projectVersionFeatures = useEvaluateStudioProjectVersionFeatures({
    studioVersion: projectStudioVersion,
  });

  const { loading, data: projectReplacementsQueryData } =
    useUserProjectReplacementsQuery({
      variables: { inputs: { projectId: projectId! } },
      skip: !projectId || projectVersionFeatures.replacementsDisabled,
      fetchPolicy,
    });
  // If the user can read team replacements, return all project replacements
  // otherwise only return personal library project replacements
  const filteredProjectReplacements = useMemo(() => {
    const projectReplacements =
      (projectReplacementsQueryData?.user.project
        ?.replacements as EnhancedPhoneticReplacement[]) ?? [];
    return projectReplacements.filter(
      replacement => canReadTeamReplacements || !replacement.libraryId
    );
  }, [projectReplacementsQueryData, canReadTeamReplacements]);

  return {
    projectReplacements: filteredProjectReplacements,
    loading,
  };
};

export default useProjectReplacements;
